import { useState, useEffect, useRef, useCallback } from 'react';
import { SubscriptionService } from '../services/SubscriptionService';

/**
 * Custom hook for managing subscription state
 */
export const useSubscription = () => {
	const [subscription, setSubscription] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const hasFetchedRef = useRef(false); // Prevent multiple fetches

	/**
	 * Fetch current subscription
	 */
	const fetchSubscription = async () => {
		try {
			setIsLoading(true);
			setError(null);
			const response = await SubscriptionService.getCurrentSubscription();
			
			if (response.status === 200) {
				// subscription can be null if user doesn't have one - that's OK
				setSubscription(response.subscription || null);
			} else {
				setSubscription(null);
			}
		} catch (err) {
			console.error('Error fetching subscription:', err);
			// Don't set error for 404 or if user just doesn't have subscription
			// Only set error for actual failures
			if (err.response?.status !== 404 && err.response?.status !== 200) {
				setError(err.message || 'Failed to fetch subscription');
			}
			setSubscription(null);
		} finally {
			setIsLoading(false);
		}
	};

	/**
	 * Initialize subscription on mount (only once)
	 */
	useEffect(() => {
		if (!hasFetchedRef.current) {
			hasFetchedRef.current = true;
			fetchSubscription();
		}
	}, []);

	/**
	 * Check if user has active subscription
	 */
	const hasActiveSubscription = () => {
		return subscription && subscription.status === 'active';
	};

	/**
	 * Get current plan name
	 */
	const getCurrentPlanName = () => {
		return subscription?.planName || null;
	};

	/**
	 * Get billing interval
	 */
	const getBillingInterval = () => {
		return subscription?.billingInterval || null;
	};

	/**
	 * Check if subscription is canceling at period end
	 */
	const isCancelingAtPeriodEnd = () => {
		return subscription?.cancelAtPeriodEnd || false;
	};

	/**
	 * Get subscription period end date
	 */
	const getPeriodEndDate = () => {
		return subscription?.currentPeriodEnd || null;
	};

	/**
	 * Refresh subscription data
	 * Memoized to prevent infinite loops in components that depend on it
	 */
	const refreshSubscription = useCallback(() => {
		return fetchSubscription();
	}, []); // Empty deps - fetchSubscription is stable

	return {
		subscription,
		isLoading,
		error,
		hasActiveSubscription,
		getCurrentPlanName,
		getBillingInterval,
		isCancelingAtPeriodEnd,
		getPeriodEndDate,
		refreshSubscription
	};
};

